import type { AppConfig } from "../config";
import type {
  RunRequest,
  TaskStatus,
  StructuredResult,
  TokenInfo,
} from "../types";

export class MagiApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = "MagiApiError";
  }
}

export class TokenRejectedError extends MagiApiError {
  constructor(status: number, message = "service token rejected") {
    super(status, message);
    this.name = "TokenRejectedError";
  }
}

export class MagiClient {
  constructor(private cfg: Pick<AppConfig, "serviceBaseUrl" | "serviceToken">) {}

  private url(path: string) {
    return `${this.cfg.serviceBaseUrl}${path}`;
  }

  private headers(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = { "Content-Type": "application/json", ...extra };
    if (this.cfg.serviceToken) h.Authorization = `Bearer ${this.cfg.serviceToken}`;
    return h;
  }

  async health(): Promise<{ status: string }> {
    const r = await fetch(this.url("/api/health/"), { headers: this.headers() });
    if (!r.ok) throw new MagiApiError(r.status, "health check failed");
    return r.json();
  }

  async getTokenInfo(): Promise<TokenInfo> {
    const r = await fetch(this.url("/api/magi/token/info"), {
      headers: this.headers(),
    });
    if (r.status === 401 || r.status === 403) {
      throw new TokenRejectedError(r.status);
    }
    if (!r.ok) throw new MagiApiError(r.status, "token info failed");
    return r.json();
  }

  async submit(req: RunRequest): Promise<{ task_id: string; stream_url: string }> {
    const r = await fetch(this.url("/api/magi/run_structured"), {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(req),
    });
    if (!r.ok) {
      const text = await r.text();
      throw new MagiApiError(r.status, `submit failed: ${r.status}`, text);
    }
    return r.json();
  }

  async getTask(taskId: string): Promise<TaskStatus> {
    const r = await fetch(this.url(`/api/magi/tasks/${taskId}`), {
      headers: this.headers(),
    });
    if (!r.ok) throw new MagiApiError(r.status, "fetch task failed");
    return r.json();
  }

  /**
   * Stream task progress via fetch + ReadableStream.
   * Browser's EventSource can't send Authorization headers, so we parse SSE ourselves.
   */
  async *stream(
    taskId: string,
    signal?: AbortSignal,
  ): AsyncGenerator<{ event: string; data: Record<string, unknown> }> {
    const r = await fetch(this.url(`/api/magi/tasks/${taskId}/stream`), {
      headers: this.headers({ Accept: "text/event-stream" }),
      signal,
    });
    if (!r.ok || !r.body) {
      throw new MagiApiError(r.status, "stream failed");
    }
    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      // SSE frames are separated by blank lines.
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseSseFrame(frame);
        if (parsed) yield parsed;
      }
    }
  }

  async listTasks(skip = 0, limit = 20) {
    const r = await fetch(
      this.url(`/api/magi/tasks?skip=${skip}&limit=${limit}`),
      { headers: this.headers() },
    );
    if (!r.ok) throw new MagiApiError(r.status, "list tasks failed");
    return r.json();
  }

  async checkModels(models?: string[]) {
    const r = await fetch(this.url("/api/magi/check_models"), {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ models: models ?? null }),
    });
    if (!r.ok) throw new MagiApiError(r.status, "check_models failed");
    return r.json() as Promise<{ results: Array<Record<string, unknown>> }>;
  }
}

export function parseSseFrame(
  frame: string,
): { event: string; data: Record<string, unknown> } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith(":") || line.length === 0) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  const raw = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(raw) };
  } catch {
    return { event, data: { raw } };
  }
}

export function extractStructured(result: unknown): StructuredResult | null {
  if (!result || typeof result !== "object") return null;
  const r = result as StructuredResult;
  if (Array.isArray(r.rounds)) return r;
  return null;
}
